/* global MutationObserver */

import { h } from 'preact';
import { useEffect, useRef, useState } from 'preact/hooks';
import { memo } from 'preact/compat';
import { signal } from '@preact/signals';
import { compose } from 'redux';

import Logger from '../../utils/logger';
import Tracer from '../../utils/tracer';
import loadAppManifest from '../../app-manifest/manifest-loader';
import loadAppAssets from '../../app-manifest/assets-loader';
import { registerTranslations } from '../../utils/translations';
import { NOOP } from '../../constants';

export function createCache() {
  return signal({});
}

const renderAppCache = createCache();

function registerAppLocales(localesDataList) {
  localesDataList.forEach(({ namespace, data }) => {
    registerTranslations(namespace, data);
  });
}

function subscribeToContainerRenders(node, subscriptionCallback) {
  return new Promise(resolve => {
    const callback = (mutationList, observer) => {
      mutationList.forEach(mutation => {
        if (mutation.type === 'childList' || mutation.type === 'subtree') {
          observer.disconnect();

          resolve(subscriptionCallback(node));
        }
      });
    };

    const observer = new MutationObserver(callback);

    observer.observe(node, { childList: true, subtree: true });
  });
}

async function setupAppRender(name, manifest, locale, cache) {
  const isCached = !!cache.value[name];
  const hasMatchingReleaseVersion =
    (isCached && cache.value[name].createdAt === manifest.createdAt) || false;

  if (isCached && hasMatchingReleaseVersion) return cache.value[name].render;
  const { App, translationDataList } = await loadAppAssets(
    name,
    manifest,
    locale,
  );

  registerAppLocales(translationDataList);

  const render = (node, args, callback) =>
    new Promise(res => {
      subscribeToContainerRenders(
        node,
        compose(
          res,
          callback,
        ),
      );

      App.create({ container: node, ...args });
    });

  // eslint-disable-next-line no-param-reassign
  cache.value[name] = {
    render,
    destroy: App.destroy || NOOP,
    createdAt: manifest.createdAt,
  };

  return render;
}

const Content = ({
  name,
  manifest,
  onAction,
  settings,
  apiClient,
  el,
  cache,
  loadingStart,
  ...props
}) => {
  const ref = useRef(null);

  const appLoadedCallback = () => {
    Tracer.addAction(`${name}.micro_app.loaded`, {
      microApp: { loadingTimeInMs: Date.now() - loadingStart },
    });
  };

  useEffect(() => () => cache.value[name]?.destroy(), []);

  useEffect(
    () => {
      async function renderApp() {
        const render = await setupAppRender(
          name,
          manifest,
          settings.locale,
          cache,
        );

        const renderOptions = {
          ...props,
          apiClient,
          settings,
          onAction,
          useHashHistory: true,
        };

        return render(ref.current, renderOptions, appLoadedCallback);
      }

      if (ref.current) {
        renderApp().catch(error => {
          Logger.error('MicroApp Error: Failed to load app assets', {
            app: name,
            args: JSON.stringify({ ...props, settings }),
            raisedError: {
              name: error.name,
              message: error.message,
            },
          });
        });
      }
    },
    [ref.current, props],
  );

  return h(el, { ref }, null);
};

const MicroApp = ({
  el = 'div',
  cache = renderAppCache,
  loadingStart = Date.now(),
  name,
  ...props
}) => {
  const [manifest, setManifest] = useState(null);

  const updateManifest = newManifest => {
    setManifest(newManifest);
  };

  useEffect(() => {
    loadAppManifest(name)
      .then(updateManifest)
      .catch(error => {
        Logger.error('MicroApp Error: Failed to load app manifest', {
          app: name,
          args: JSON.stringify(props),
          raisedError: {
            name: error.name,
            message: error.message,
          },
        });
      });
  }, []);

  return manifest ? (
    <Content
      name={name}
      el={el}
      manifest={manifest}
      cache={cache}
      loadingStart={loadingStart}
      {...props}
    />
  ) : null;
};

export default memo(MicroApp);
